import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { z } from "zod"
import { toast } from "sonner"
import { Trash2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FilePondDocumentUpload } from "@/components/file-pond-upload"
import { useDeleteFile } from "@/utils/api/file-service"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type SupportingDocumentDraft,
  type SupportingDocumentStatus,
} from "../api/useMembershipApplicationDraft"
import { MEMBERSHIP_APPLICATION_BASE_PATH, membershipApplicationSteps } from "../constants"
import { getCategoryDocumentRequirements } from "../data/category-requirements"
import { useApplicationSectionConfiguration } from "../hooks/useApplicationConfiguration"


const DOCUMENT_STATUS_VALUES = ["pending", "uploaded"] as const satisfies readonly SupportingDocumentStatus[]

const documentEntrySchema = z.object({
  id: z.string().optional(),
  documentType: z.string().trim().min(2, "Describe the document"),
  fileId: z.string().optional(),
  fileUrl: z.string().optional(),
  fileName: z.string().optional(), // Keep for backward compatibility
  originalFilename: z.string().optional(),
  status: z.enum(DOCUMENT_STATUS_VALUES),
  // notes: z.string().trim().max(260, "Keep notes under 260 characters").optional(),
  mandatory: z.boolean().optional(),
})

const documentsDraftSchema = z.object({
  documents: z.array(documentEntrySchema),
})

const documentsFormSchema = documentsDraftSchema.superRefine((data, ctx) => {
  if (data.documents.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Add at least one supporting document",
      path: ["documents"],
    })
    return
  }

  const allUploaded = data.documents.every((doc) => doc.status === "uploaded")
  if (!allUploaded) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Mark each document as uploaded once attached",
      path: ["documents"],
    })
  }
})

type DocumentsDraftValues = z.infer<typeof documentsDraftSchema>
export type DocumentsFormValues = z.infer<typeof documentsDraftSchema>

const DEFAULT_RECOMMENDED_DOCUMENTS = [
  "Passport photograph",
  "Curriculum Vitae",
  "National ID / Passport",
  "Academic transcript",
]

function cryptoId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch (_error) {
    // ignore
  }
  return Math.random().toString(36).slice(2, 12)
}

function mapDraftToFormValues(
  documents: SupportingDocumentDraft[],
  options?: { seedRecommended?: boolean; recommendedSeeds?: string[]; mandatoryLabels?: Set<string> },
): DocumentsDraftValues {
  const { seedRecommended = true, recommendedSeeds = DEFAULT_RECOMMENDED_DOCUMENTS, mandatoryLabels = new Set() } = options ?? {}
  if (!documents || documents.length === 0) {
    return {
      documents:
        seedRecommended
          ? recommendedSeeds.map((type) => ({
              id: cryptoId(),
              documentType: type,
              fileId: undefined,
              fileUrl: undefined,
              fileName: "",
              originalFilename: "",
              status: "pending" as const,
              // notes: "",
              mandatory: mandatoryLabels.has(type),
            }))
          : [],
    }
  }

  return {
    documents: documents.map((doc) => ({
      id: doc.id,
      documentType: doc.documentType ?? "",
      fileId: doc.fileId ?? undefined,
      fileUrl: doc.fileUrl ?? undefined,
      fileName: doc.fileName ?? "",
      originalFilename: doc.originalFilename ?? doc.fileName ?? "",
      status: doc.status ?? "pending",
      // notes: doc.notes ?? "",
      mandatory: doc.mandatory ?? mandatoryLabels.has(doc.documentType),
    })),
  }
}

function mapFormToDraft(values: DocumentsDraftValues): SupportingDocumentDraft[] {
  return values.documents.map((doc) => ({
    id: doc.id ?? cryptoId(),
    documentType: doc.documentType.trim(),
    fileId: doc.fileId?.trim(),
    fileUrl: doc.fileUrl?.trim() || undefined,
    fileName: doc.originalFilename?.trim() || doc.fileName?.trim() || undefined,
    originalFilename: doc.originalFilename?.trim() || doc.fileName?.trim() || undefined,
    status: doc.status,
    // notes: doc.notes?.trim() || undefined,
    mandatory: doc.mandatory,
  }))
}

function createEmptyDocument(mandatory = false) {
  return {
    id: cryptoId(),
    documentType: "",
    fileId: undefined,
    fileUrl: undefined,
    fileName: "",
    originalFilename: "",
    status: "pending" as const,
    // notes: "",
    mandatory,
  }
}

export function DocumentsStep() {
  const navigate = useNavigate()
  const { data: draft } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()
  const deleteFileMutation = useDeleteFile()

  const categoryId = draft?.category?.categoryId ?? null
  const {
    section,
    isVisible,
    isRequired,
    nextStep,
    categoriesQuery,
    category,
  } = useApplicationSectionConfiguration("documents", categoryId)

  // Get category-specific document requirements from API
  const categoryDocumentRequirements = useMemo(
    () => getCategoryDocumentRequirements(category),
    [category],
  )

  const recommendedSeeds = useMemo(() => {
    // Combine section requirements and category-specific requirements
    const sectionLabels = (section?.documentRequirements ?? []).map((req) => req.label)
    const categoryLabels = categoryDocumentRequirements.map((req: { label: string }) => req.label)
    const allLabels = [...new Set([...categoryLabels, ...sectionLabels])]
    
    return allLabels.length > 0 ? allLabels : DEFAULT_RECOMMENDED_DOCUMENTS
  }, [section?.documentRequirements, categoryDocumentRequirements])

  // Create a set of mandatory document labels from category requirements
  const mandatoryDocumentLabels = useMemo(
    () => new Set<string>(
      categoryDocumentRequirements
        .filter((req: { mandatory?: boolean }) => req.mandatory)
        .map((req: { label: string }) => req.label)
    ),
    [categoryDocumentRequirements],
  )

  useEffect(() => {
    if (categoriesQuery.isLoading) {
      return
    }
    if (!isVisible) {
      const fallbackPath = nextStep?.path ?? membershipApplicationSteps[membershipApplicationSteps.length - 1]?.path
      if (fallbackPath) {
        navigate({ to: fallbackPath, replace: true })
      }
    }
  }, [categoriesQuery.isLoading, isVisible, nextStep, navigate])

  const formDefaultValues = useMemo(
    () =>
      mapDraftToFormValues(draft?.supportingDocuments ?? [], {
        seedRecommended: isRequired,
        recommendedSeeds,
        mandatoryLabels: mandatoryDocumentLabels,
      }),
    [draft?.supportingDocuments, isRequired, recommendedSeeds, mandatoryDocumentLabels],
  )

  const schema = isRequired ? documentsFormSchema : documentsDraftSchema
  const resolver = useMemo(() => zodResolver(schema), [schema])

  const form = useForm<DocumentsFormValues>({
    resolver,
    defaultValues: formDefaultValues,
    mode: "onChange",
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "documents",
  })

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasLocalChangesRef = useRef(false)

  // Hydrate loaded drafts/configuration until editing starts. Autosave echoes
  // must not reset the field array: reset replaces row keys and loses focus.
  useEffect(() => {
    if (!hasLocalChangesRef.current) {
      form.reset(formDefaultValues)
    }
  }, [formDefaultValues, form])

  // Autosave effect
  useEffect(() => {
    const subscription = form.watch((values, info) => {
      // Resets have no field name. Include named programmatic changes from
      // adding/removing rows and uploading files, as well as typed changes.
      if (!info.name) {
        return
      }
      hasLocalChangesRef.current = true

      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }

      autosaveTimerRef.current = setTimeout(() => {
        const parsed = documentsDraftSchema.safeParse(values)
        if (!parsed.success) {
          return
        }

        const payload = mapFormToDraft(parsed.data)
        saveDraftMutation.mutate({ supportingDocuments: payload })
      }, 800)
    })

    return () => {
      subscription.unsubscribe()
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepPath =
    nextStep?.path ??
    membershipApplicationSteps.find((step) => step.id === "review")?.path ??
    membershipApplicationSteps[membershipApplicationSteps.length - 1]?.path ??
    `${MEMBERSHIP_APPLICATION_BASE_PATH}/review`

  const [removingDocIndex, setRemovingDocIndex] = useState<number | null>(null)

  const handleDeleteFile = useCallback(
    async (index: number) => {
      const documentEntry = form.watch(`documents.${index}`)
      const fileId = documentEntry?.fileId ?? documentEntry?.id ?? null

      if (!fileId) {
        toast.error("No file ID found")
        return
      }

      try {
        setRemovingDocIndex(index)
        await deleteFileMutation.mutateAsync({ fileId })
        
        // Clear file-related fields
        form.setValue(`documents.${index}.fileId`, undefined)
        form.setValue(`documents.${index}.fileUrl`, undefined)
        form.setValue(`documents.${index}.fileName`, "")
        form.setValue(`documents.${index}.originalFilename`, "")
        form.setValue(`documents.${index}.status`, "pending")
        
        toast.success("File deleted successfully")
      } catch (error) {
        console.error(error)
        toast.error("Failed to delete file. Please try again.")
      } finally {
        setRemovingDocIndex(null)
      }
    },
    [deleteFileMutation, form],
  )

  const handleSubmit = useCallback(
    async (values: DocumentsFormValues) => {
      const payload = mapFormToDraft(values)
      await saveDraftMutation.mutateAsync({ supportingDocuments: payload })
      navigate({ to: nextStepPath })
    },
    [navigate, nextStepPath, saveDraftMutation],
  )

  const isBusy = isSavingDraft || saveDraftMutation.isPending

  if (!isVisible && !categoriesQuery.isLoading) {
    return null
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">
            Supporting documents
            {!isRequired ? (
              <span className="ml-2 text-sm font-medium text-muted-foreground">Optional</span>
            ) : null}
          </h2>
          <p className="text-sm text-muted-foreground">
            {section?.guidance ??
              "Prepare the documents required for your membership application. Upload handling will be wired to the document service in the next iteration."}
          </p>
          {!isRequired ? (
            <p className="text-xs text-muted-foreground">
              {category?.categoryName
                ? `${category.categoryName} applicants may skip this section if they do not have documents to provide yet.`
                : "You may skip this section if you do not have documents to provide yet."}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={() => append(createEmptyDocument(false))}>
            Add document
          </Button>
        </div>

        <div className="space-y-4">
          {fields.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No documents listed yet. Add the documents you intend to upload.
              </CardContent>
            </Card>
          ) : null}

          {fields.map((field, index) => {
            const isMandatory = field.mandatory === true
            const documentEntry = form.watch(`documents.${index}`)
            // console.log(documentEntry);
            
            const existingFileName =
              documentEntry?.originalFilename?.trim() || documentEntry?.fileName?.trim() || ""
            const existingFileUrl = documentEntry?.fileUrl?.trim()
            const existingStatus = documentEntry?.status
            const showExistingFile = Boolean(existingFileName || existingFileUrl || existingStatus === "uploaded")
            
            return (
            <Card key={field.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base">
                    Document {index + 1}
                    {isMandatory && <span className="ml-2 text-xs font-normal text-destructive">* Required</span>}
                  </CardTitle>
                  <CardDescription>
                    {isMandatory
                      ? "This document is required by your selected category and cannot be removed."
                      : "Track the document and mark it uploaded once attached."}
                  </CardDescription>
                </div>
                {!isMandatory && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-destructive"
                  >
                    Remove
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name={`documents.${index}.documentType`}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel required>Document type</FormLabel>
                      <FormControl>
                        <Input disabled={isMandatory} placeholder="e.g. Passport photograph" data-invalid={fieldState.invalid} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {showExistingFile ? (
                  <div className="rounded-md border border-dashed border-muted bg-muted/40 p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">
                          {existingFileName || "Uploaded document"}
                        </p>
                        <div className="flex items-center gap-3">
                          {existingFileUrl ? (
                            <a
                              href={existingFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-medium text-primary underline underline-offset-4"
                            >
                              View current file
                            </a>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Previously uploaded for this application.
                            </p>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFile(index)}
                            disabled={removingDocIndex === index}
                            className="h-auto p-0 text-xs text-destructive hover:text-destructive/80"
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            {removingDocIndex === index ? "Deleting..." : "Delete file"}
                          </Button>
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className={
                          existingStatus === "uploaded"
                            ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-200"
                            : undefined
                        }
                      >
                        {existingStatus === "uploaded" ? "Uploaded" : "Pending"}
                      </Badge>
                    </div>
                  </div>
                ) : null}

                {!showExistingFile && <FormItem>
                  <FormLabel required={isMandatory}>Upload file</FormLabel>
                  <FormControl>
                    <FilePondDocumentUpload
                      maxFileSize="20MB"
                      required={isMandatory}
                      isAuthenticated={false}
                      usernameOrEmail={draft?.profile?.email ?? undefined}
                      onUploadComplete={(fileId, fileUrl, filename) => {
                        form.setValue(`documents.${index}.fileId`, fileId)
                        form.setValue(`documents.${index}.fileUrl`, fileUrl)
                        form.setValue(`documents.${index}.fileName`, filename)
                        form.setValue(`documents.${index}.originalFilename`, filename)
                        form.setValue(`documents.${index}.status`, "uploaded")
                      }}
                      onUploadError={(error) => {
                        form.setError(`documents.${index}.fileId`, {
                          type: "manual",
                          message: error,
                        })
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>}
              </CardContent>
            </Card>
          )
          })}
        </div>

        {form.formState.errors.documents ? (
          <p className="text-sm text-destructive">{form.formState.errors.documents.message}</p>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" disabled={isBusy}>
            {isBusy ? "Saving..." : "Proceed to review"}
          </Button>
        </div>
      </form>
    </Form>
  )
}
